import { useEffect, useMemo, useRef, useState } from 'react';
import { scrollToElementById } from '../../lib/utils/common';
import { FacetState, FacetValue } from '@coveo/headless';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SfAccordionSummary, SfLocationLabelText } from './Filter.style';
import ButtonDiscreet from '../shared/Button/ButtonDiscreet';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UiBox from '../shared/Box';
import { TypographyProps } from '@mui/material/Typography';

const RelatedTermsFilter = (props: any): JSX.Element => {
  const theme = useTheme();
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down(960));
  const { controller, clearAllText, noFacetsText, showMoreText, showLessText } = props;
  const [state, setState] = useState<FacetState>(controller.state);
  const shouldScrollToRelatedTerms = useRef(false);

  const onShowLess = () => {
    shouldScrollToRelatedTerms.current = true;
    controller.showLessValues();
  };

  const onClearAll = () => {
    controller.deselectAll();
  };
  const onChange = (value: any) => {
    controller.toggleSelect(value);
  };

  const isClearBtnShown = useMemo(() => {
    const activeFacetsWithResults = state.values.filter(
      (value: FacetValue) => value.state === 'selected' && value.numberOfResults
    );

    return state.hasActiveValues && activeFacetsWithResults.length > 0;
  }, [state.hasActiveValues, state.values]);

  useEffect(() => controller.subscribe(() => setState(controller.state)), [controller]);

  useEffect(() => {
    if (shouldScrollToRelatedTerms.current && state.canShowMoreValues) {
      shouldScrollToRelatedTerms.current = false;
      scrollToElementById('related-terms-header');
    }
  }, [state.canShowMoreValues, state.values.length]);

  // useEffect(() => {
  //   const conditionValue = headlessEngine.state.query?.q;
  //   if (!conditionValue) return;

  //   const matchingValue = state.values.find(
  //     (item) => item.value.toLowerCase() === conditionValue.toLowerCase().trim()
  //   );

  //   if (matchingValue) {
  //     controller.toggleSelect({ value: matchingValue.value, state: 'selected' });
  //   }
  // }, [headlessEngine.state.query?.q]);

  return (
    <Grid item sx={{ marginTop: '1rem' }}>
      <Accordion disableGutters defaultExpanded={isTabletOrMobile ? false : true}>
        <SfAccordionSummary
          expandIcon={
            <ChevronRightIcon
              sx={{
                color: (theme: any) => theme.palette.neutral8.main,
                height: '32px',
                width: '32px',
              }}
            />
          }
          aria-controls="related-terms-content"
          id="related-terms-header"
        >
          <SfLocationLabelText>{props?.fields?.heading?.value}</SfLocationLabelText>
        </SfAccordionSummary>

        {isClearBtnShown && (
          <Typography
            onClick={onClearAll}
            component="div"
            color="error.main"
            sx={{
              marginLeft: '16px',
              '&:hover': { cursor: 'pointer', textDecoration: 'underline' },
            }}
            {...({} as TypographyProps<'div'>)} // ✅ This ensures correct type inference
          >
            {clearAllText}
          </Typography>
        )}

        <AccordionDetails sx={{ paddingBottom: '0' }}>
          {state && state.values.length ? (
            state.values
              .filter((value: FacetValue) => value.numberOfResults)
              .map((value: FacetValue) => {
                return (
                  <div key={value?.value}>
                    <FormControlLabel
                      sx={{
                        color: (theme) => theme.palette.neutral8.main,
                        marginBottom: '0.125rem',
                        alignItems: 'flex-start',
                        '.MuiFormControlLabel-label': {
                          display: 'inline-block',
                          paddingTop: '11px',
                        },
                      }}
                      key={value.value}
                      label={`${value.value} (${value.numberOfResults} results)`}
                      control={
                        <Checkbox
                          checked={controller.isValueSelected(value)}
                          onChange={() => onChange(value)}
                          disabled={state.isLoading}
                        />
                      }
                    />
                  </div>
                );
              })
          ) : (
            <span>{noFacetsText}</span>
          )}
        </AccordionDetails>
        {/* TODO: hard coded string need to remove */}
        {state.canShowLessValues && !state.canShowMoreValues && (
          <UiBox styles={{ padding: '0 0 0.5rem 0.5rem' }}>
            <ButtonDiscreet onClick={onShowLess} iconLeft={<RemoveIcon />} gap="0">
              {showLessText || 'Show Less'}
            </ButtonDiscreet>
          </UiBox>
        )}
        {state.canShowMoreValues && (
          <UiBox styles={{ padding: '0 0 0.5rem 0.5rem' }}>
            <ButtonDiscreet
              onClick={() => controller.showMoreValues()}
              iconLeft={<AddIcon />}
              gap="0"
            >
              {showMoreText || 'Show More'}
            </ButtonDiscreet>
          </UiBox>
        )}
      </Accordion>
    </Grid>
  );
};

export default RelatedTermsFilter;
